/**
 * App shell layout lifted from the prototype (DEC-016): AntD `<Layout>` with a dark
 * sider (dark in both themes — DEC-025), a header carrying the user pill (which owns the
 * theme and language selectors), and an `<Outlet>` for routed content. Each routed page
 * renders inside a per-route error boundary (R-18.60) and a `<Suspense>` fallback
 * (R-18.140) so a single route's failure or lazy-load never blanks the shell.
 *
 * No hardcoded colours (R-18.18): surfaces come from the ConfigProvider theme tokens
 * (`useToken`); only structural sizing is inline.
 */

import {
  ApartmentOutlined,
  CloudSyncOutlined,
  FileDoneOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  BgColorsOutlined,
  BugOutlined,
  CalendarOutlined,
  CarOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  EnvironmentOutlined,
  EuroOutlined,
  ExperimentOutlined,
  FileOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  HistoryOutlined,
  InboxOutlined,
  KeyOutlined,
  NumberOutlined,
  PartitionOutlined,
  ProfileOutlined,
  ReadOutlined,
  ShoppingOutlined,
  TeamOutlined,
  SafetyOutlined,
  SearchOutlined,
  MailOutlined,
  PhoneOutlined,
  SettingOutlined,
  ToolOutlined,
  UnorderedListOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Layout, Menu, Spin, theme, Typography } from 'antd';
import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { UserPill } from './UserPill';

import type { MenuProps } from 'antd';

import { NotificationBell } from '@/features/notifications';
import { MyOverdueDialog } from '@/features/work-orders';
import { useAuth } from '@/shared/auth/AuthProvider';
import { DiagnosticsDrawer } from '@/shared/diagnostics/DiagnosticsDrawer';
import { usePanelStore } from '@/shared/stores/panel-store';
import { useTelephonyMe } from '@/shared/telephony';
import { RouteErrorBoundary } from '@/shared/ui/AppErrorBoundary';

const { Header, Sider, Content } = Layout;

// The Calls counter, lazy so the telephony feature stays out of the shell's chunk.
const CallsReviewBadge = lazy(() =>
  import('@/features/telephony').then((m) => ({ default: m.CallsReviewBadge })),
);
const CallbacksBadge = lazy(() =>
  import('@/features/telephony').then((m) => ({ default: m.CallbacksBadge })),
);
const ActiveCallWindow = lazy(() =>
  import('@/features/telephony').then((m) => ({ default: m.ActiveCallWindow })),
);

/** "Who is calling" (LMT calls plan §5.8a): the telephony chunk loads only for a user who may see the card. */
function ActiveCallSlot(): React.JSX.Element | null {
  const me = useTelephonyMe();
  const data = me.data;
  if (data === undefined || !data.screenPopAvailable || !data.screenPop || data.employee === null) return null;
  return (
    <Suspense fallback={null}>
      <ActiveCallWindow />
    </Suspense>
  );
}

/**
 * Sidebar navigation modelled on the prototype shell's grouped structure: top-level
 * entries plus nested `Inventory`, `Documents`, and `Admin` submenus. Each leaf carries
 * the read capability that gates it; a submenu is hidden entirely when the user lacks
 * every child capability. `cap: null` (Dashboard) is always shown. Labels are i18n keys
 * resolved at render so the tree can live at module scope.
 *
 * A leaf may be `disabled` — a placeholder shown inactive (greyed, not navigable). The
 * Inventory submenu mirrors the prototype's Physical / Virtual / Log section layout but
 * only "Owned vehicles" is wired to a real page; the rest are disabled until built.
 */
interface NavLeaf {
  key: string;
  icon: React.ReactNode;
  labelKey: string;
  /** The key that shows the entry; a list shows it to a holder of ANY of them (work orders: read own / all / write). */
  cap: string | readonly string[] | null;
  /**
   * Hidden from anyone who HOLDS this capability: the entry is a fallback for the roles that cannot
   * reach the surface the feature normally lives on (Service stays, see the Storage group).
   */
  hiddenWithCap?: string;
  /** Shown but inactive — no `<Link>`, greyed out (feature not yet built). */
  disabled?: boolean;
  /** A counter after the label (loaded lazily with its feature). */
  badge?: React.ComponentType;
}

/** A non-clickable heading (`type: 'group'`) grouping leaves inside a submenu. */
interface NavSection {
  type: 'group';
  key: string;
  labelKey: string;
  children: NavLeaf[];
}

interface NavGroup {
  key: string;
  icon: React.ReactNode;
  labelKey: string;
  /** When set, the whole submenu is hidden unless the user holds this capability. */
  cap?: string | null;
  children: (NavLeaf | NavSection)[];
}

type NavNode = NavLeaf | NavGroup;

/** Who opens the work orders: one's own (a performer), every order, or the editors (API P1-13.3). */
// The warehouse reads work orders to issue parts to them (S5, `wh_mgr`: warehouse.write).
const WORK_ORDER_READERS = ['service.read', 'service.read.all', 'service.wo.write', 'warehouse.write'] as const;

function capsOf(cap: string | readonly string[] | null): readonly string[] {
  return cap === null ? [] : typeof cap === 'string' ? [cap] : cap;
}

function isGroup(node: NavNode): node is NavGroup {
  return 'children' in node;
}

function isSection(node: NavLeaf | NavSection): node is NavSection {
  return (node as NavSection).type === 'group';
}

/**
 * Section-heading style for the non-clickable group labels inside the Inventory submenu
 * (e.g. "Physical" / "Virtual" / "Log"), lifted from the prototype: small, uppercase,
 * slightly transparent so they read as dividers rather than menu items.
 */
const sectionHeadingStyle: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  opacity: 0.55,
  fontWeight: 600,
};

const navTree: NavNode[] = [
  { key: '/', icon: <DashboardOutlined />, labelKey: 'nav.dashboard', cap: null },
  { key: '/vehicles', icon: <CarOutlined />, labelKey: 'nav.vehicles', cap: 'vehicles.read' },
  { key: '/customers', icon: <TeamOutlined />, labelKey: 'nav.customers', cap: 'customers.read' },
  { key: '/sales', icon: <ShoppingOutlined />, labelKey: 'nav.sales', cap: 'sales.read' },
  {
    key: 'storage',
    icon: <InboxOutlined />,
    labelKey: 'nav.storage',
    // No group-level cap: the section now holds two modules with SEPARATE capabilities (service
    // stays, seed 47), and `tech` / `svc_mgr` hold `service-stays.read` without `storage.read`.
    // A group-level gate would hide their only entry; each leaf is still gated on its own key, and
    // a group whose every leaf is hidden is dropped anyway.
    cap: null,
    children: [
      {
        key: '/storage-intakes',
        icon: <FileTextOutlined />,
        labelKey: 'nav.storageAgreements',
        cap: 'storage.read',
      },
      {
        key: '/storage-locations',
        icon: <EnvironmentOutlined />,
        labelKey: 'nav.storageLocations',
        cap: 'storage.read',
      },
      // The flat list of the cars live contracts cover (feedback 23.08, decision 8) — the section
      // held only AGREEMENTS, which cannot answer "is this car with us right now?".
      {
        key: '/storage-vehicles',
        icon: <CarOutlined />,
        labelKey: 'nav.storageVehicles',
        cap: 'storage.read',
      },
      // Cars in the zone WITHOUT a contract (service-stay plan M3). Since 18.09.2026 the list is a
      // tab of the Agreements page (the client wants one zone on one screen), so this entry serves
      // ONLY the roles that hold `service-stays.read` without `storage.read` (`tech`, `svc_mgr`,
      // seed 47) — the agreements page is closed to them, and this is their way in.
      {
        key: '/service-stays',
        icon: <ToolOutlined />,
        labelKey: 'nav.serviceStays',
        cap: 'service-stays.read',
        hiddenWithCap: 'storage.read',
      },
      // Customer tire sets in custody (API schema/174, LIAA 6c) — its own key, so a tire keeper
      // without the vehicle-storage keys still sees the Storage group through this one leaf.
      {
        key: '/tire-storage',
        icon: <InboxOutlined />,
        labelKey: 'nav.tireStorage',
        cap: 'tire-storage.read',
      },
    ],
  },
  // The service module (LIAA service plan, API schema/180+). S1 brings the dictionaries; the work orders,
  // the planner, the unclosed panel and the reports join the group in their own stages. No group-level
  // cap: each leaf is gated on its own key and a group whose every leaf is hidden is dropped.
  {
    key: 'service',
    icon: <ToolOutlined />,
    labelKey: 'nav.service',
    cap: null,
    children: [
      { key: '/work-orders', icon: <FileDoneOutlined />, labelKey: 'nav.workOrders', cap: WORK_ORDER_READERS },
      // The planner (S6): whoever sees every order; bookings by service.booking.write.
      { key: '/service/planner', icon: <CalendarOutlined />, labelKey: 'nav.servicePlanner', cap: ['service.read.all', 'service.wo.write', 'service.booking.write'] },
      // The unclosed orders panel (S7.4): whoever sees every order.
      { key: '/service/unclosed', icon: <FileDoneOutlined />, labelKey: 'nav.serviceUnclosed', cap: 'service.read.all' },
      // The service reports (S10): workload and revenue.
      { key: '/service/reports', icon: <FileDoneOutlined />, labelKey: 'nav.serviceReports', cap: 'service.reports' },
      // Exit approvals (S8.2): whoever may override the exit block.
      { key: '/exit-approvals', icon: <FileDoneOutlined />, labelKey: 'nav.exitApprovals', cap: 'storage.exit-block.override' },
      // The performer's phone panel (S3.2).
      { key: '/my-work', icon: <FileDoneOutlined />, labelKey: 'nav.myWork', cap: ['service.work.own', 'service.wo.write', 'service.time.manage'] },
      { key: '/service/catalog', icon: <UnorderedListOutlined />, labelKey: 'nav.serviceCatalog', cap: 'service.settings' },
      { key: '/service/work-types', icon: <AppstoreOutlined />, labelKey: 'nav.serviceWorkTypes', cap: 'service.settings' },
      { key: '/service/staff', icon: <TeamOutlined />, labelKey: 'nav.serviceStaff', cap: 'service.settings' },
      { key: '/service/workplaces', icon: <EnvironmentOutlined />, labelKey: 'nav.serviceWorkplaces', cap: 'service.settings' },
      { key: '/service/statuses', icon: <PartitionOutlined />, labelKey: 'nav.serviceStatuses', cap: 'service.settings' },
      { key: '/service/settings', icon: <SettingOutlined />, labelKey: 'nav.serviceSettings', cap: 'service.settings' },
    ],
  },
  // The rental module (rental plan M2/M6): the resource calendar, the bookings and the pool.
  {
    key: 'rental',
    icon: <KeyOutlined />,
    labelKey: 'nav.rental',
    cap: 'rental.read',
    children: [
      {
        key: '/rental',
        icon: <CalendarOutlined />,
        labelKey: 'nav.rentalCalendar',
        cap: 'rental.read',
      },
      {
        key: '/rental/agreements',
        icon: <FileTextOutlined />,
        labelKey: 'nav.rentalAgreements',
        cap: 'rental.read',
      },
      {
        key: '/rental/fleet',
        icon: <CarOutlined />,
        labelKey: 'nav.rentalFleet',
        cap: 'rental.read',
      },
    ],
  },
  // Phone calls from LMT Zvanu pārvaldnieks (plan 2026_10_31_lmt_calls §5.6, §5.7a).
  {
    key: 'communications',
    icon: <PhoneOutlined />,
    labelKey: 'nav.communications',
    cap: 'telephony.calls.read',
    children: [
      {
        key: '/communications/calls',
        icon: <PhoneOutlined />,
        labelKey: 'nav.calls',
        cap: 'telephony.calls.read',
        badge: CallsReviewBadge,
      },
      {
        key: '/communications/callbacks',
        icon: <PhoneOutlined />,
        labelKey: 'nav.callbacks',
        cap: 'telephony.calls.read',
        badge: CallbacksBadge,
      },
    ],
  },
  {
    key: 'inventory',
    icon: <DatabaseOutlined />,
    labelKey: 'nav.inventory',
    // No group-level cap (since the warehouse, API schema/173): the group shows when ANY child is
    // visible. A warehouse keeper holds `warehouse.read` without `inventory.read` — the dealer's own
    // car fleet and purchasing — and must see the parts screens without being handed that access.
    cap: null,
    children: [
      {
        type: 'group',
        key: 'inventory-parts',
        labelKey: 'nav.inventoryParts',
        children: [
          {
            key: '/parts/new',
            icon: <AppstoreOutlined />,
            labelKey: 'nav.inventoryNewParts',
            cap: 'warehouse.read',
          },
          {
            // Used parts and every RRR.LT-linked card (W5, schema/176–177).
            key: '/parts/used',
            icon: <AppstoreOutlined />,
            labelKey: 'nav.inventoryUsedParts',
            cap: 'warehouse.read',
          },
          {
            key: '/parts/search',
            icon: <SearchOutlined />,
            labelKey: 'nav.inventoryCodeSearch',
            cap: 'warehouse.read',
          },
        ],
      },
      {
        type: 'group',
        key: 'inventory-operations',
        labelKey: 'nav.inventoryOperations',
        children: [
          {
            key: '/warehouse/documents',
            icon: <FileTextOutlined />,
            labelKey: 'nav.inventoryStockDocuments',
            cap: 'warehouse.read',
          },
          {
            key: '/warehouse/movements',
            icon: <AuditOutlined />,
            labelKey: 'nav.inventoryMovements',
            cap: 'warehouse.read',
          },
          {
            key: '/warehouse/locations',
            icon: <ApartmentOutlined />,
            labelKey: 'nav.inventoryWarehouses',
            cap: 'warehouse.read',
          },
        ],
      },
      {
        type: 'group',
        key: 'inventory-integrations',
        labelKey: 'nav.inventoryIntegrations',
        children: [
          {
            // The RRR.LT (Ovoko) sync panel (W5, design 2.3): read with warehouse.read, act with
            // warehouse.rrr.publish (checked by the page and the API).
            key: '/warehouse/rrr',
            icon: <CloudSyncOutlined />,
            labelKey: 'nav.inventoryRrr',
            cap: 'warehouse.read',
          },
        ],
      },
      {
        type: 'group',
        key: 'inventory-physical',
        labelKey: 'nav.inventoryPhysical',
        children: [
          {
            key: '/parts/tires',
            icon: <BgColorsOutlined />,
            labelKey: 'nav.inventoryTires',
            cap: 'inventory.read',
            disabled: true,
          },
          {
            key: '/inventory/owned-vehicles',
            icon: <CarOutlined />,
            labelKey: 'nav.inventoryOwnedVehicles',
            cap: 'inventory.read',
          },
        ],
      },
      {
        type: 'group',
        key: 'inventory-virtual',
        labelKey: 'nav.inventoryVirtual',
        children: [
          {
            key: '/inventory/virtual-vehicles',
            icon: <CarOutlined />,
            labelKey: 'nav.inventoryVehicleInventory',
            cap: 'inventory.read',
            disabled: true,
          },
          {
            key: '/inventory/virtual-employees',
            icon: <UserOutlined />,
            labelKey: 'nav.inventoryEmployeeInventory',
            cap: 'inventory.read',
            disabled: true,
          },
        ],
      },
    ],
  },
  {
    key: 'finance',
    icon: <EuroOutlined />,
    labelKey: 'nav.finance',
    children: [
      { key: '/finance/outgoing', icon: <EuroOutlined />, labelKey: 'nav.financeOutgoing', cap: 'finance.read' },
      { key: '/finance/incoming', icon: <EuroOutlined />, labelKey: 'nav.financeIncoming', cap: 'finance.read' },
      // The draft-invoice tray (schema/118) — accumulating drafts + the unbilled pool.
      { key: '/finance/drafts', icon: <FileTextOutlined />, labelKey: 'nav.financeDrafts', cap: 'finance.read' },
      {
        key: '/finance/expenses',
        icon: <ProfileOutlined />,
        labelKey: 'nav.financeExpenses',
        cap: 'finance.read',
        disabled: true,
      },
      {
        key: '/finance/reports',
        icon: <FileTextOutlined />,
        labelKey: 'nav.financeReports',
        cap: 'finance.read',
        disabled: true,
      },
      // The accounting export (jumis-integration-plan §3.12). Its own capability, not `finance.read`:
      // seeing bills is not the same permission as pushing them into somebody's books.
      {
        key: '/finance/jumis',
        icon: <AuditOutlined />,
        labelKey: 'nav.financeJumis',
        cap: 'finance.jumis.read',
      },
      // The bank payments read from Jumis that wait for a decision (Jumis closing plan A5).
      {
        key: '/finance/bank-payments',
        icon: <BankOutlined />,
        labelKey: 'nav.financeBankPayments',
        cap: 'finance.bank-import',
      },
    ],
  },
  {
    key: 'documents',
    icon: <FileOutlined />,
    labelKey: 'nav.documents',
    children: [
      {
        key: '/document-templates',
        icon: <FileTextOutlined />,
        labelKey: 'nav.templates',
        cap: 'templates.read',
      },
      {
        key: '/entity-documents',
        icon: <ProfileOutlined />,
        labelKey: 'nav.entityDocuments',
        cap: 'templates.read',
      },
      {
        key: '/documents',
        icon: <FolderOpenOutlined />,
        labelKey: 'nav.filledArchive',
        cap: 'documents.read',
      },
    ],
  },
  {
    key: 'admin',
    icon: <SettingOutlined />,
    labelKey: 'nav.admin',
    children: [
      { key: '/admin/users', icon: <TeamOutlined />, labelKey: 'nav.users', cap: 'users.read' },
      { key: '/admin/roles', icon: <SafetyOutlined />, labelKey: 'nav.roles', cap: 'roles.read' },
      {
        key: '/admin/settings',
        icon: <SettingOutlined />,
        labelKey: 'nav.settings',
        cap: 'settings.read',
      },
      {
        key: '/admin/communications',
        icon: <MailOutlined />,
        labelKey: 'nav.communications',
        cap: 'communications.manage',
      },
      {
        key: '/admin/telephony',
        icon: <PhoneOutlined />,
        labelKey: 'nav.telephony',
        cap: 'telephony.manage',
      },
      { key: '/admin/audit', icon: <AuditOutlined />, labelKey: 'nav.audit', cap: 'audit.read' },
      {
        key: '/admin/error-log',
        icon: <BugOutlined />,
        labelKey: 'nav.errorLog',
        cap: 'errors.read',
      },
      {
        key: '/admin/generators',
        icon: <NumberOutlined />,
        labelKey: 'nav.generators',
        cap: 'generators.read',
      },
      {
        key: '/admin/locations',
        icon: <EnvironmentOutlined />,
        labelKey: 'nav.locations',
        cap: 'locations.read',
      },
      {
        key: '/admin/company-details',
        icon: <BankOutlined />,
        labelKey: 'nav.companyDetails',
        cap: 'company-details.read',
      },
      {
        key: '/admin/pickup-locations',
        icon: <CarOutlined />,
        labelKey: 'nav.pickupLocations',
        cap: 'rental-locations.read',
      },
      {
        key: '/admin/storage-packages',
        icon: <InboxOutlined />,
        labelKey: 'nav.storagePackages',
        cap: 'storage-packages.read',
      },
      {
        // Д-33 — the checklist-position dictionary. Rides on the settings capability, like the other
        // application reference data configured in Admin (no capability of its own to seed).
        key: '/admin/complectation',
        icon: <UnorderedListOutlined />,
        labelKey: 'nav.complectation',
        cap: 'settings.read',
      },
      {
        key: '/admin/jobs',
        icon: <ClockCircleOutlined />,
        labelKey: 'nav.scheduledJobs',
        cap: 'jobs.read',
      },
      { key: '/admin/utils', icon: <ToolOutlined />, labelKey: 'nav.utils', cap: 'utils.read' },
      {
        key: '/admin/ad-mappings',
        icon: <DatabaseOutlined />,
        labelKey: 'nav.adMappings',
        cap: 'ad-mappings.read',
      },
      {
        key: '/admin/workflows',
        icon: <PartitionOutlined />,
        labelKey: 'nav.workflows',
        cap: 'workflows.read',
      },
      {
        // Placeholder admin page. Rides on the utils capability — the same admin-tooling
        // audience — since it has no capability of its own to seed.
        key: '/admin/testing',
        icon: <ExperimentOutlined />,
        labelKey: 'nav.testing',
        cap: 'utils.read',
      },
    ],
  },
  // Informational page for every authenticated user (no capability gate) — the product
  // backlog + release changelog.
  {
    key: '/backlog-changelog',
    icon: <HistoryOutlined />,
    labelKey: 'nav.backlogChangelog',
    cap: null,
  },
  // Last entry by design: the illustrated how-to guides. Also ungated — an instruction is for
  // whoever has to follow it, and it reveals nothing a user cannot already see on the screens.
  {
    key: '/instructions',
    icon: <ReadOutlined />,
    labelKey: 'nav.instructions',
    cap: null,
  },
];

/**
 * "Can this user use the MAIN panel?" (gate-panel plan, decision 5) — true when they hold at least
 * one capability the nav tree above gates on. The ungated entries (Dashboard, instructions) don't
 * count: they are also reachable from the gate panel's shell, so alone they don't make the main
 * panel worth landing on. Shared by the post-login panel routing and the gate panel's back-switcher.
 */
export function canUseMainPanel(hasCapability: (key: string) => boolean): boolean {
  const caps = new Set<string>();
  for (const node of navTree) {
    if (isGroup(node)) {
      if (node.cap != null) caps.add(node.cap);
      for (const child of node.children) {
        if (isSection(child)) {
          for (const leaf of child.children) for (const key of capsOf(leaf.cap)) caps.add(key);
        } else {
          for (const key of capsOf(child.cap)) caps.add(key);
        }
      }
    } else {
      for (const key of capsOf(node.cap)) caps.add(key);
    }
  }
  return [...caps].some(hasCapability);
}

/** Every leaf key under a group, flattened across nested sections. */
function groupLeafKeys(group: NavGroup): string[] {
  return group.children.flatMap((child) =>
    isSection(child) ? child.children.map((leaf) => leaf.key) : [child.key],
  );
}

/** Submenu keys whose subtree contains the active route — so direct-URL nav auto-expands. */
function activeGroupKeys(pathname: string): string[] {
  return navTree
    .filter(isGroup)
    .filter((g) =>
      groupLeafKeys(g).some((key) => pathname === key || pathname.startsWith(`${key}/`)),
    )
    .map((g) => g.key);
}

/** Every leaf key across top-level entries, submenus, and nested sections. */
function allLeafKeys(): string[] {
  return navTree.flatMap((node) => (isGroup(node) ? groupLeafKeys(node) : [node.key]));
}

/**
 * The menu key to highlight for the current route. A leaf key matches when it equals the
 * pathname or is a path-segment prefix of it (`/vehicles` ← `/vehicles/show/:id`), so
 * detail/child routes keep their section highlighted. The longest match wins, so a more
 * specific leaf beats a shorter sibling; `/` (Dashboard) only ever matches exactly.
 */
function selectedLeafKey(pathname: string): string | undefined {
  return allLeafKeys()
    .filter((key) => pathname === key || pathname.startsWith(`${key}/`))
    .sort((a, b) => b.length - a.length)[0];
}

export function AppLayout(): React.JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const setPanel = usePanelStore((s) => s.setPanel);
  const { token } = theme.useToken();
  // Defense-in-depth nav gating (PROD-004 / PA-web-capability-gating.1): an admin entry the
  // user lacks the read capability for is not rendered. `cap: null` (Home) is always shown.
  // The backend still 403s every endpoint independently (PA-web-capability-gating.2).
  const { hasCapability } = useAuth();

  // Auto-expand the submenu containing the active route on direct-URL navigation, while
  // still letting the user collapse/expand submenus manually (onOpenChange).
  const [openKeys, setOpenKeys] = useState<string[]>(() => activeGroupKeys(location.pathname));
  useEffect(() => {
    const active = activeGroupKeys(location.pathname);
    setOpenKeys((prev) => Array.from(new Set([...prev, ...active])));
  }, [location.pathname]);

  // Every in-app navigation lands at the TOP of the new page. The window is the scroller (the
  // shell has no inner overflow container) and React Router carries the previous scroll offset
  // across a route change, so a link clicked far down a long list — a table row, a card link,
  // a nav entry reached past the fold — used to open the next page at its middle instead of
  // its heading. Keyed on the pathname alone, so the URL-driven sub-views and filters that
  // live in the query string (`?view=`, `?status=`, pagination) leave the page where it is.
  // A drawer opened as a child route (`/vehicles/create`, `/customers/show/:id/edit`) counts
  // as a navigation too: the list behind the overlay is reset to its top.
  //
  // The ref skips the initial mount so a browser reload keeps its own restored scroll offset;
  // only an actual pathname change scrolls.
  const lastPathname = useRef(location.pathname);
  useEffect(() => {
    if (lastPathname.current === location.pathname) return;
    lastPathname.current = location.pathname;
    window.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);

  // Diagnostics drawer (Ctrl+Shift+D) — the per-tab API call log. Deliberately a hotkey and
  // not a nav entry: it is a support tool ("press Ctrl+Shift+D and send me the copy"), needs
  // no capability, and must stay reachable from whatever screen just failed. The listener is
  // on the document so it works regardless of focus, and it ignores repeats from a held key.
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.ctrlKey && event.shiftKey && !event.repeat && event.key.toLowerCase() === 'd') {
        // Chrome binds Ctrl+Shift+D to "bookmark all tabs"; claim the combination.
        event.preventDefault();
        setDiagnosticsOpen((open) => !open);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const showLeaf = (leaf: NavLeaf): boolean =>
    (leaf.cap === null || capsOf(leaf.cap).some(hasCapability)) &&
    (leaf.hiddenWithCap === undefined || !hasCapability(leaf.hiddenWithCap));

  type MenuItem = NonNullable<MenuProps['items']>[number];

  // A disabled leaf is a greyed placeholder (feature not built) — no `<Link>`, so it
  // never navigates; an active leaf carries a `<Link>` AntD renders as an anchor.
  const buildLeaf = (leaf: NavLeaf): MenuItem | null => {
    if (!showLeaf(leaf)) return null;
    if (leaf.disabled) {
      return { key: leaf.key, icon: leaf.icon, label: t(leaf.labelKey), disabled: true };
    }
    const Counter = leaf.badge;
    return {
      key: leaf.key,
      icon: leaf.icon,
      label: (
        <Link to={leaf.key}>
          {t(leaf.labelKey)}
          {Counter !== undefined && (
            <Suspense fallback={null}>
              <Counter />
            </Suspense>
          )}
        </Link>
      ),
    };
  };

  const menuItems = navTree
    .map((node): MenuItem | null => {
      if (isGroup(node)) {
        // A group-level cap (Inventory) hides the whole submenu when the user lacks it.
        if (node.cap != null && !hasCapability(node.cap)) return null;
        const children = node.children
          .map((child): MenuItem | null => {
            if (!isSection(child)) return buildLeaf(child);
            const sectionChildren = child.children
              .map(buildLeaf)
              .filter((item): item is MenuItem => item !== null);
            if (sectionChildren.length === 0) return null;
            return {
              type: 'group',
              key: child.key,
              label: <span style={sectionHeadingStyle}>{t(child.labelKey)}</span>,
              children: sectionChildren,
            };
          })
          .filter((item): item is MenuItem => item !== null);
        if (children.length === 0) return null;
        return { key: node.key, icon: node.icon, label: t(node.labelKey), children };
      }
      return buildLeaf(node);
    })
    .filter((item): item is MenuItem => item !== null);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* `theme="dark"` keeps the sider dark under both algorithms (DEC-025). */}
      <Sider theme="dark" breakpoint="lg" collapsedWidth={0}>
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
          <Typography.Text
            strong
            style={{ color: token.colorTextLightSolid, display: 'block', padding: 16 }}
          >
            {t('app.name')}
          </Typography.Text>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedLeafKey(location.pathname) ?? location.pathname]}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            items={menuItems}
          />
        </div>
      </Sider>
      <Layout>
        <Header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 16,
            paddingInline: 16,
            background: token.colorBgElevated,
          }}
        >
          {/* The switch into the mobile gate panel (gate-panel plan, decision 5; moved into the
              account menu by feedback 21.08): visible only with gate.access; remembers the choice
              on this device before navigating. */}
          <NotificationBell />
          <UserPill
            panelSwitch={
              hasCapability('gate.access')
                ? {
                    label: t('nav.gatePanel'),
                    onClick: () => {
                      setPanel('gate');
                      navigate('/gate');
                    },
                  }
                : null
            }
          />
        </Header>
        {/* AntD `<Content>` renders as a `<main>` element (R-18.29) — the single page
            landmark wrapping the routed `<Outlet>`; the axe `region` rule (R-18.32) is
            satisfied by the shipped DOM, no test-only wrapper. Exactly one `<main>` per page. */}
        {/* One's own late work orders, at sign-in (P1-2.5, 31.07: cannot be switched off). */}
        <MyOverdueDialog />
        <Content style={{ margin: 16 }}>
          <RouteErrorBoundary>
            <Suspense fallback={<Spin description={t('app.loading')} />}>
              <Outlet />
            </Suspense>
          </RouteErrorBoundary>
        </Content>
      </Layout>
      <DiagnosticsDrawer open={diagnosticsOpen} onClose={() => setDiagnosticsOpen(false)} />
      <ActiveCallSlot />
    </Layout>
  );
}
